import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { GenieCategories } from "@/types/shared/categories";
import { SetupAnalysisResult } from '@/types/agent/setup';
import { ReviewSectionCard } from "./ReviewSectionCard";
import { getFieldSuggestions } from "../../utils/smartSuggestions";

interface ConfirmationIdentitySectionProps {
  analysis: SetupAnalysisResult;
  onChange: (field: keyof SetupAnalysisResult, value: any) => void;
}

const SUPPORTED_LANGUAGES = [
  { code: "en", label: "English" },
  { code: "es", label: "Spanish" },
  { code: "fr", label: "French" },
  { code: "de", label: "German" },
  { code: "it", label: "Italian" },
  { code: "pt", label: "Portuguese" },
  { code: "pl", label: "Polish" },
  { code: "nl", label: "Dutch" },
  { code: "ja", label: "Japanese" },
  { code: "zh", label: "Chinese" },
  { code: "ko", label: "Korean" },
  { code: "ar", label: "Arabic" },
  { code: "hi", label: "Hindi" },
];

export const ConfirmationIdentitySection = ({ analysis, onChange }: ConfirmationIdentitySectionProps) => {
  const category = GenieCategories.find((cat) => cat.id === analysis.category) || GenieCategories.find((cat) => cat.id === 'other')!;

  return (
    <ReviewSectionCard
      title="Tell us about your genie"
      subtitle="This helps customers understand what your genie does"
      genieIcon="brain"
      categoryColor={category.color}
    >
      <div className="space-y-8">
        {/* Genie Name */}
        <div>
          <Label htmlFor="genieName" className="text-base sm:text-lg font-semibold text-gray-900 block mb-2">
            What should we call your genie? <span className="text-red-500">*</span>
          </Label>
          <p className="text-xs sm:text-sm text-gray-600 mb-3">
            Choose a friendly name your customers will recognize (3-50 characters)
          </p>
          <Input
            id="genieName"
            value={analysis.genieName}
            onChange={(e) => onChange("genieName", e.target.value)}
            className="text-sm sm:text-base p-3 sm:p-4 h-10 sm:h-12"
            placeholder="e.g., Sales Assistant, Support Helper"
            maxLength={50}
          />
          <div className="flex justify-between mt-1">
            <span className="text-xs text-gray-500">Required field</span>
            <span className="text-xs text-gray-500">{analysis.genieName?.length || 0}/50</span>
          </div>
        </div>

        {/* Category */}
        <div>
          <Label htmlFor="category" className="text-base sm:text-lg font-semibold text-gray-900 block mb-2">
            What category best fits? <span className="text-red-500">*</span>
          </Label>
          <p className="text-xs sm:text-sm text-gray-600 mb-3">
            This helps organize and discover your genie
          </p>
          <Select value={analysis.category} onValueChange={(value) => onChange("category", value)}>
            <SelectTrigger className="h-10 sm:h-12 text-sm sm:text-base">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {GenieCategories.map((cat) => (
                <SelectItem key={cat.id} value={cat.id}>
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-gray-500 mt-1 block">Required field</span>
        </div>

        {/* Use Case / Description */}
        <div>
          <Label htmlFor="useCase" className="text-base sm:text-lg font-semibold text-gray-900 block mb-2">
            What will your genie help customers do?
          </Label>
          <p className="text-xs sm:text-sm text-gray-600 mb-3">
            Example: Schedule appointments and answer product questions
          </p>
          <div className="relative">
            <Textarea
              id="useCase"
              value={analysis.useCase}
              onChange={(e) => onChange("useCase", e.target.value)}
              className="text-sm p-3 min-h-[100px]"
              rows={3}
              placeholder={getFieldSuggestions('useCase', analysis)}
            />
            {!analysis.useCase && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute bottom-2 right-2 text-xs"
                onClick={() => onChange("useCase", getFieldSuggestions('useCase', analysis))}
              >
                Use AI Suggestion
              </Button>
            )}
          </div>
        </div>

        {/* Language Selector */}
        <div>
          <Label htmlFor="language" className="text-base sm:text-lg font-semibold text-gray-900 block mb-2">
            What language should it speak?
          </Label>
          <p className="text-xs sm:text-sm text-gray-600 mb-3">
            Select the primary language for conversations
          </p>
          <Select
            value={analysis.language || "en"}
            onValueChange={(value) => onChange("language", value)}
          >
            <SelectTrigger className="h-10 sm:h-12 text-sm sm:text-base">
              <SelectValue placeholder="Select language..." />
            </SelectTrigger>
            <SelectContent>
              {SUPPORTED_LANGUAGES.map((lang) => (
                <SelectItem key={lang.code} value={lang.code}>
                  {lang.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Public Genie Toggle */}
        <div className="border-t pt-8">
          <div className="flex items-center justify-between gap-4">
            <div>
              <Label className="text-base sm:text-lg font-semibold text-gray-900 block mb-1">
                Make this genie private?
              </Label>
              <p className="text-xs sm:text-sm text-gray-600">
                Keep this genie visible to only you (genies are public by default)
              </p>
            </div>
            <Switch
              checked={!analysis.isPublic}
              onCheckedChange={(checked) => onChange("isPublic", !checked)}
              className="data-[state=checked]:bg-[#FF4DB8]"
            />
          </div>
        </div>
      </div>
    </ReviewSectionCard>
  );
};
